import { useEffect } from "react";

/**
 * Directly manages <head> SEO tags. react-helmet-async in this build reliably
 * updates only <title>, so every public page calls this hook instead.
 *
 * Phase 6A guarantee: EXACTLY ONE authoritative title, meta description and
 * canonical per route. The hook ADOPTS the static tags shipped in index.html
 * (homepage defaults) and mutates them in place — it never appends duplicates.
 * Pass `canonical: null` (or set `noindex`) to REMOVE the canonical entirely
 * so the homepage canonical can never leak onto not-found/private views.
 */
export function useSeoHeadTags({ title, description, canonical, ogImage, ogType = "article", noindex = false, jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    // Adopt-or-create: prefer a tag we already manage, else claim the static
    // tag from index.html, else create one. Guarantees a single instance.
    const claim = (selector, create) => {
      let el = document.querySelector(`${selector}[data-shs-seo="1"]`) || document.querySelector(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      el.setAttribute("data-shs-seo", "1");
      return el;
    };

    const setMeta = (attr, value, content) => {
      if (!content) return;
      const el = claim(`meta[${attr}="${value}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute(attr, value);
        return m;
      });
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Canonical: exactly one when provided; none at all when absent/noindex.
    if (canonical && !noindex) {
      const el = claim('link[rel="canonical"]', () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      });
      el.setAttribute("href", canonical);
    } else {
      document.querySelectorAll('link[rel="canonical"]').forEach((l) => l.remove());
    }

    // Robots directive — mutate the static robots meta for noindex pages and
    // restore the stashed original when leaving them.
    const staticRobots = Array.from(document.querySelectorAll('meta[name="robots"]'))
      .find((m) => !m.hasAttribute("data-shs-seo"));
    let managedRobots = document.querySelector('meta[name="robots"][data-shs-seo="1"]');
    if (noindex) {
      if (!managedRobots && !staticRobots) {
        managedRobots = document.createElement("meta");
        managedRobots.setAttribute("name", "robots");
        managedRobots.setAttribute("data-shs-seo", "1");
        document.head.appendChild(managedRobots);
      }
      if (managedRobots) managedRobots.setAttribute("content", "noindex, nofollow");
      if (staticRobots) {
        if (!staticRobots.hasAttribute("data-shs-seo-stashed")) {
          staticRobots.setAttribute("data-shs-seo-stashed", staticRobots.getAttribute("content") || "");
        }
        staticRobots.setAttribute("content", "noindex, nofollow");
      }
    } else {
      if (managedRobots) managedRobots.remove();
      if (staticRobots && staticRobots.hasAttribute("data-shs-seo-stashed")) {
        staticRobots.setAttribute("content", staticRobots.getAttribute("data-shs-seo-stashed"));
        staticRobots.removeAttribute("data-shs-seo-stashed");
      }
    }

    // JSON-LD structured data (remove previous managed instance, add new one)
    document.querySelectorAll('script[type="application/ld+json"][data-shs-seo="1"]').forEach((s) => s.remove());
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-shs-seo", "1");
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }

    return () => {
      // Tags persist across SPA navigation — the next page's hook call mutates
      // them. Only the JSON-LD is removed so rich-snippet data can't leak.
      document.querySelectorAll('script[type="application/ld+json"][data-shs-seo="1"]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, ogImage, ogType, noindex, JSON.stringify(jsonLd || null)]);
}
